import { Business as BusinessIcon, LocationOn as LocationIcon } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Paper,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatPhoneDisplay } from '@sophocles/utils/phoneUtils';

/**
 * ContactSavePromptDialog - Prompt user to save/update contact after editing
 *
 * Shows a dialog when user has modified contact/address information,
 * allowing them to:
 * - Update existing contact (only for saved contacts)
 * - Save as new contact (with customer/site group level selection)
 * - Use once without saving (inline data only, FK=null)
 */
const ContactSavePromptDialog = ({
  open,
  originalContact,
  editedData,
  isManualEntry = false,
  onSaveNew,
  onUpdate,
  onSkip,
  onClose,
  customerName = 'Customer',
  siteGroupName = 'Site Group',
}) => {
  const { t } = useTranslation();
  const [saveLevel, setSaveLevel] = useState('site_group'); // 'customer' or 'site_group'
  const [showLevelSelector, setShowLevelSelector] = useState(false);

  if (!editedData) {
    return null;
  }

  const isSavedContact = !isManualEntry && originalContact?.id;

  // Check what actually changed
  const hasAnyChanges =
    originalContact &&
    (originalContact.name !== editedData.name ||
      originalContact.business_name !== editedData.business_name ||
      originalContact.email !== editedData.email ||
      originalContact.phone1 !== editedData.phone1 ||
      originalContact.address !== editedData.address ||
      originalContact.address_2 !== editedData.address_2 ||
      originalContact.city !== editedData.city ||
      originalContact.state !== editedData.state ||
      originalContact.zipcode !== editedData.zipcode ||
      originalContact.country !== editedData.country ||
      originalContact.is_primary_rma_billing !== editedData.is_primary_rma_billing ||
      originalContact.is_primary_rma_shipping !== editedData.is_primary_rma_shipping ||
      originalContact.is_primary_sales_billing !== editedData.is_primary_sales_billing ||
      originalContact.is_primary_sales_shipping !== editedData.is_primary_sales_shipping);

  const addressChanged =
    originalContact &&
    (originalContact.address !== editedData.address ||
      originalContact.address_2 !== editedData.address_2 ||
      originalContact.city !== editedData.city ||
      originalContact.state !== editedData.state ||
      originalContact.zipcode !== editedData.zipcode);

  // Format address for display
  const formatAddress = contact => {
    if (!contact) return '';
    const parts = [
      contact.address,
      contact.address_2,
      [contact.city, contact.state, contact.zipcode].filter(Boolean).join(', '),
    ].filter(Boolean);
    return parts.join(', ');
  };

  const handleSaveNew = () => {
    if (!showLevelSelector && isSavedContact) {
      // First click on "Save as New" - show level selector
      setShowLevelSelector(true);
    } else {
      // Level selected or new contact - proceed with save
      onSaveNew(saveLevel);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {showLevelSelector
          ? t('contacts.savePrompt.titleSaveNew', 'Save as New Contact')
          : isSavedContact
            ? t('contacts.savePrompt.titleUpdate', 'Save Changes?')
            : t('contacts.savePrompt.titleSave', 'Save Contact?')}
      </DialogTitle>

      <DialogContent>
        {/* Contact Preview Card */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('contacts.savePrompt.contactInfo', 'Contact Information')}
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {editedData.name || editedData.business_name || 'Unnamed Contact'}
          </Typography>
          {editedData.business_name && editedData.name && (
            <Typography variant="body2" color="text.secondary">
              {editedData.business_name}
            </Typography>
          )}
          {editedData.email && <Typography variant="body2">{editedData.email}</Typography>}
          {editedData.phone1 && <Typography variant="body2">{formatPhoneDisplay(editedData.phone1)}</Typography>}
          {(editedData.address || editedData.city) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {formatAddress(editedData)}
            </Typography>
          )}
        </Paper>

        {/* Show original address only if address actually changed */}
        {isSavedContact && addressChanged && !showLevelSelector && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('contacts.savePrompt.originalAddress', 'Original address:')}
            </Typography>
            <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
              {formatAddress(originalContact)}
            </Typography>
          </Box>
        )}

        {/* Level Selector - Only show when saving as NEW (after clicking "Save as New") */}
        {(showLevelSelector || !isSavedContact) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('contacts.savePrompt.saveToLevel', 'Save contact to:')}
              </Typography>
              <ToggleButtonGroup
                value={saveLevel}
                exclusive
                onChange={(e, value) => value && setSaveLevel(value)}
                fullWidth
                size="small"
                sx={{ mt: 1 }}
              >
                <ToggleButton value="site_group" sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <LocationIcon fontSize="small" />
                    <Typography variant="caption" sx={{ textTransform: 'none' }}>
                      {siteGroupName || t('contacts.savePrompt.siteGroup', 'Site Group')}
                    </Typography>
                  </Box>
                </ToggleButton>
                <ToggleButton value="customer" sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <BusinessIcon fontSize="small" />
                    <Typography variant="caption" sx={{ textTransform: 'none' }}>
                      {customerName || t('contacts.savePrompt.customer', 'Customer')}
                    </Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {saveLevel === 'site_group'
                  ? t('contacts.savePrompt.siteGroupHint', 'Contact will be available for this site group only')
                  : t(
                      'contacts.savePrompt.customerHint',
                      'Contact will be available for all site groups under this customer',
                    )}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        {showLevelSelector ? (
          <>
            <Button onClick={() => setShowLevelSelector(false)} color="inherit" sx={{ color: 'text.secondary' }}>
              {t('common.back', 'Back')}
            </Button>
            <Button onClick={() => onSaveNew(saveLevel)} variant="contained">
              {t('contacts.savePrompt.save', 'Save Contact')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onSkip} color="inherit" sx={{ color: 'text.secondary' }}>
              {t('contacts.savePrompt.useOnce', 'Use Once Only')}
            </Button>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {isSavedContact && (
                <>
                  <Button onClick={handleSaveNew} variant="outlined">
                    {t('contacts.savePrompt.saveAsNew', 'Save as New')}
                  </Button>
                  <Button onClick={onUpdate} variant="contained" disabled={!hasAnyChanges}>
                    {t('contacts.savePrompt.updateExisting', 'Update Existing')}
                  </Button>
                </>
              )}

              {!isSavedContact && (
                <Button onClick={handleSaveNew} variant="contained">
                  {t('contacts.savePrompt.save', 'Save Contact')}
                </Button>
              )}
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

ContactSavePromptDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  originalContact: PropTypes.object,
  editedData: PropTypes.shape({
    name: PropTypes.string,
    business_name: PropTypes.string,
    email: PropTypes.string,
    phone1: PropTypes.string,
    address: PropTypes.string,
    address_2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipcode: PropTypes.string,
    country: PropTypes.string,
  }),
  isManualEntry: PropTypes.bool,
  onSaveNew: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  customerName: PropTypes.string,
  siteGroupName: PropTypes.string,
};

ContactSavePromptDialog.defaultProps = {
  originalContact: null,
  editedData: null,
  customerName: 'Customer',
  siteGroupName: 'Site Group',
};

export default ContactSavePromptDialog;
